import React, { useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'

import { parseDocxXml } from '@/lib/cat/docxXmlParser'
import { parseXliff } from '@/lib/cat/xliffParser'
import { analyzeNewVersion, type VersionAnalysis } from '@/lib/cat/versionAnalyzer'
import {
  Upload, FileText, CheckCircle, AlertCircle,
  Zap, Plus, ArrowRight, Loader2
} from 'lucide-react'

type Step = 'upload' | 'analyzing' | 'report' | 'creating'

export default function NewVersionPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [step, setStep] = useState<Step>('upload')
  const [dragOver, setDragOver] = useState(false)
  const [error, setError] = useState('')
  const [parentProject, setParentProject] = useState<any>(null)
  const [analysis, setAnalysis] = useState<VersionAnalysis | null>(null)
  const [fileName, setFileName] = useState('')
  const [wordCount, setWordCount] = useState(0)

  React.useEffect(() => {
    if (!projectId || !user) return
    supabase
      .from('cat_projects')
      .select('*')
      .eq('id', projectId)
      .single()
      .then(({ data }) => setParentProject(data))
  }, [projectId, user])

  const parseFile = async (file: File) => {
    const ext = file.name.toLowerCase().split('.').pop()

    if (ext === 'docx') {
      const xmlSegments = await parseDocxXml(file)
      const wc = xmlSegments.reduce(
        (sum, seg) => sum + seg.source.split(' ').filter(Boolean).length, 0
      )
      return {
        segments: xmlSegments.map(seg => ({
          source: seg.source,
          paragraphIndex: seg.paragraphIndex,
        })),
        wordCount: wc,
      }
    }

    if (ext === 'xliff' || ext === 'xlf') {
      const content = await file.text()
      const result = parseXliff(content)
      const wc = result.segments.reduce(
        (sum, seg) => sum + seg.source.split(' ').filter(Boolean).length, 0
      )
      return {
        segments: result.segments.map(seg => ({ source: seg.source })),
        wordCount: wc,
      }
    }

    if (ext === 'txt') {
      const content = await file.text()
      const parts = content.split(/\n\s*\n/).filter(p => p.trim())
      const segs = parts.length > 1
        ? parts.map(p => ({ source: p.trim() }))
        : content.split(/[.!?]+\s+/).filter(s => s.trim().length > 10).map(s => ({ source: s.trim() }))
      const wc = segs.reduce((sum, s) => sum + s.source.split(' ').filter(Boolean).length, 0)
      return { segments: segs, wordCount: wc }
    }

    throw new Error('Unsupported file type. Please upload DOCX, XLIFF, or TXT.')
  }

  const handleFile = async (file: File) => {
    if (!user || !projectId || !parentProject) return
    setError('')
    setFileName(file.name)
    setStep('analyzing')

    try {
      const parsed = await parseFile(file)

      if (!parsed.segments.length) {
        throw new Error('No text segments found in the document.')
      }

      setWordCount(parsed.wordCount)

      const result = await analyzeNewVersion(
        parsed.segments,
        projectId,
        user.id,
        parentProject.source_language,
        parentProject.target_language
      )

      setAnalysis(result)
      setStep('report')
    } catch (err: any) {
      setError(err.message || 'Failed to analyse document.')
      setStep('upload')
    }
  }

  const createVersion = async () => {
    if (!user || !projectId || !analysis || !parentProject) return
    setStep('creating')
    setError('')

    try {
      // Determine next version number
      const { data: siblings } = await supabase
        .from('cat_projects')
        .select('version_number')
        .eq('parent_project_id', projectId)
        .order('version_number', { ascending: false })
        .limit(1)

      const nextVersion = siblings?.length
        ? (siblings[0].version_number || 1) + 1
        : 2

      const { data: newProject, error: projErr } = await supabase
        .from('cat_projects')
        .insert({
          name: `${parentProject.name} — v${nextVersion}`,
          source_language: parentProject.source_language,
          target_language: parentProject.target_language,
          owner_id: user.id,
          total_segments: analysis.segments.length,
          completed_segments: analysis.stats.confirmed,
          word_count: wordCount,
          status: 'active',
          file_url: parentProject.file_url || null,
          parent_project_id: projectId,
          version_number: nextVersion,
          version_status: 'active',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select()
        .single()

      if (projErr) throw new Error(projErr.message)

      const segmentsToInsert = analysis.segments.map(seg => ({
        project_id: newProject.id,
        segment_order: seg.segment_order,
        source_text: seg.source_text,
        target_text: seg.target_text,
        xml_paragraph_index: seg.xml_paragraph_index ?? null,
        status: seg.match_type === 'confirmed'
          ? 'completed'
          : seg.match_type === 'fuzzy'
            ? 'in_progress'
            : 'new',
        locked: seg.match_type === 'confirmed',
        notes: seg.match_score > 0 && seg.match_score < 100
          ? `${seg.match_score}% TM match`
          : '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }))

      const { error: segErr } = await supabase
        .from('cat_segments')
        .insert(segmentsToInsert)

      if (segErr) throw new Error(segErr.message)

      navigate(`/projects/${newProject.id}/editor`)
    } catch (err: any) {
      setError(err.message || 'Failed to create version.')
      setStep('report')
    }
  }

  const StatCard = ({
    label, count, borderColor, bgColor, icon, sublabel
  }: {
    label: string; count: number; borderColor: string; bgColor: string
    icon: React.ReactNode; sublabel: string
  }) => (
    <div className={`rounded-xl border-2 ${borderColor} ${bgColor} p-5`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">{label}</span>
        {icon}
      </div>
      <div className="text-4xl font-bold text-gray-900 mb-1">{count}</div>
      <div className="text-xs text-gray-500">{sublabel}</div>
    </div>
  )

  return (
    <div className="max-w-2xl mx-auto px-6 py-8">

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <span className="font-medium">{parentProject?.name || '…'}</span>
            <ArrowRight className="w-3 h-3" />
            <span className="text-gold-600 font-semibold">New Version</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Upload Revised Document</h1>
          <p className="text-gray-500 mt-1 text-sm">
            Upload the updated file — TranslyxCAT will automatically leverage your existing translations.
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Upload */}
        {step === 'upload' && (
          <div
            className={`border-2 border-dashed rounded-2xl p-16 text-center cursor-pointer transition-colors ${
              dragOver
                ? 'border-gold-400 bg-gold-50'
                : 'border-gray-300 hover:border-gold-400 hover:bg-gold-50'
            }`}
            onDragOver={e => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); e.dataTransfer.files[0] && handleFile(e.dataTransfer.files[0]) }}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg font-semibold text-gray-700 mb-1">Drop revised document here</p>
            <p className="text-sm text-gray-500 mb-3">or click to browse</p>
            <p className="text-xs text-gray-400">Supports DOCX · XLIFF · TXT</p>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".docx,.xliff,.xlf,.txt"
              onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])}
            />
          </div>
        )}

        {/* Analysing */}
        {step === 'analyzing' && (
          <div className="text-center py-20">
            <Loader2 className="w-12 h-12 text-gold-500 animate-spin mx-auto mb-4" />
            <p className="text-lg font-semibold text-gray-700">Analysing document…</p>
            <p className="text-sm text-gray-500 mt-1">Matching segments against your Translation Memory</p>
          </div>
        )}

        {/* Report */}
        {step === 'report' && analysis && (
          <div className="space-y-6">

            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
              <FileText className="w-8 h-8 text-gold-600 shrink-0" />
              <div>
                <p className="font-semibold text-gray-900">{fileName}</p>
                <p className="text-sm text-gray-500">
                  {analysis.stats.total} segments · {wordCount.toLocaleString()} words
                </p>
              </div>
              <div className="ml-auto text-right">
                <p className="text-3xl font-bold text-gold-600">{analysis.stats.leverage_percent}%</p>
                <p className="text-xs text-gray-500">TM leverage</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <StatCard
                label="Confirmed" count={analysis.stats.confirmed}
                borderColor="border-green-200" bgColor="bg-green-50"
                icon={<CheckCircle className="w-5 h-5 text-green-500" />}
                sublabel="100% match · auto-filled"
              />
              <StatCard
                label="Fuzzy" count={analysis.stats.fuzzy}
                borderColor="border-gold-200" bgColor="bg-gold-50"
                icon={<Zap className="w-5 h-5 text-gold-500" />}
                sublabel="75–99% · review needed"
              />
              <StatCard
                label="New" count={analysis.stats.new}
                borderColor="border-red-200" bgColor="bg-red-50"
                icon={<Plus className="w-5 h-5 text-red-500" />}
                sublabel="No match · needs translation"
              />
            </div>

            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800">
              <strong>What happens next:</strong> A new project version will be created.
              Confirmed segments are pre-filled and locked. Fuzzy matches are pre-filled
              and flagged for review. New segments are empty and ready for translation.
            </div>

            <button
              onClick={createVersion}
              className="w-full py-3 bg-gold-500 hover:bg-gold-400 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <ArrowRight className="w-5 h-5" />
              Create Version &amp; Open Editor
            </button>

            <button
              onClick={() => { setStep('upload'); setAnalysis(null) }}
              className="w-full py-2 text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
              ← Upload a different file
            </button>
          </div>
        )}

        {/* Creating */}
        {step === 'creating' && (
          <div className="text-center py-20">
            <Loader2 className="w-12 h-12 text-gold-500 animate-spin mx-auto mb-4" />
            <p className="text-lg font-semibold text-gray-700">Creating version…</p>
            <p className="text-sm text-gray-500 mt-1">Setting up your pre-filled segments</p>
          </div>
        )}

    </div>
  )
}
